<div class="monthly-attendance-report-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>
        <mat-icon>assessment</mat-icon>
        Monthly Attendance Report
      </mat-card-title>
      <mat-card-subtitle>
        Generate detailed monthly attendance reports with employee daily hours and totals
      </mat-card-subtitle>
    </mat-card-header>
    
    <mat-card-content>
      <!-- Month/Year Selector -->
      <div class="report-controls">
        <mat-form-field appearance="outline" class="month-selector">
          <mat-label>Month</mat-label>
          <mat-select [(value)]="selectedMonth" (selectionChange)="onMonthYearChange()">
            <mat-option *ngFor="let month of months" [value]="month.value">
              {{ month.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="year-selector">
          <mat-label>Year</mat-label>
          <mat-select [(value)]="selectedYear" (selectionChange)="onMonthYearChange()">
            <mat-option *ngFor="let year of years" [value]="year">
              {{ year }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <button mat-raised-button color="primary" (click)="loadReport()" [disabled]="loading">
          <mat-icon>refresh</mat-icon>
          Load Report
        </button>

        <button mat-raised-button color="accent" (click)="exportToExcel()" [disabled]="!reportData || loading">
          <mat-icon>download</mat-icon>
          Export to Excel
        </button>
      </div>

      <!-- Loading Indicator -->
      <div *ngIf="loading" class="loading-section">
        <mat-spinner diameter="40"></mat-spinner>
        <p>Loading monthly attendance report...</p>
      </div>

      <!-- Report Table -->
      <div *ngIf="reportData && !loading" class="report-table-container">
        <div class="report-header">
          <h2>{{ reportData.monthYear }}</h2>
        </div>

        <div class="table-wrapper">
          <table class="monthly-report-table">
            <!-- Header Row -->
            <thead>
              <tr class="header-row">
                <th class="dates-header">{{ reportData.monthYear }}</th>
                <th *ngFor="let employee of reportData.employeeDailyHours" class="employee-header">
                  {{ employee.employeeName }}
                </th>
                <th class="total-header">Total Hours</th>
              </tr>
            </thead>

            <!-- Data Rows -->
            <tbody>
              <tr *ngFor="let day of getDaysInMonthArray()" class="data-row">
                <td class="date-cell">{{ formatDate(day) }}</td>
                <td *ngFor="let employee of reportData.employeeDailyHours" class="hours-cell" [class.incomplete-shift]="isIncompleteShift(employee, day)">
                  <span *ngIf="getEmployeeHoursForDay(employee, day) > 0">
                    {{ getEmployeeHoursForDay(employee, day) | number:'1.2-2' }}
                    <span *ngIf="isIncompleteShift(employee, day)" class="incomplete-indicator" title="Incomplete shift - 7 hours assumed">*</span>
                  </span>
                </td>
                <td class="daily-total-cell">
                  <span *ngIf="getDailyTotalForDay(day) > 0">
                    {{ getDailyTotalForDay(day) | number:'1.2-2' }}
                  </span>
                </td>
              </tr>
            </tbody>

            <!-- Total Row -->
            <tfoot>
              <tr class="total-row">
                <td class="total-label">Total Hours</td>
                <td *ngFor="let employee of reportData.employeeMonthlyTotals" class="employee-total-cell">
                  {{ employee.totalHours | number:'1.2-2' }}
                </td>
                <td class="grand-total-cell">
                  {{ reportData.grandTotalHours | number:'1.2-2' }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Summary Cards -->
        <div class="summary-cards">
          <mat-card class="summary-card">
            <mat-card-content>
              <div class="summary-item">
                <mat-icon>people</mat-icon>
                <div>
                  <h3>{{ reportData.employeeDailyHours.length }}</h3>
                  <p>Employees</p>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card">
            <mat-card-content>
              <div class="summary-item">
                <mat-icon>schedule</mat-icon>
                <div>
                  <h3>{{ reportData.grandTotalHours | number:'1.2-2' }}</h3>
                  <p>Total Hours</p>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="summary-card">
            <mat-card-content>
              <div class="summary-item">
                <mat-icon>calendar_month</mat-icon>
                <div>
                  <h3>{{ getDaysInMonthArray().length }}</h3>
                  <p>Days in Month</p>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Salary Calculation Section -->
        <div class="salary-calculation-section">
          <mat-card>
            <mat-card-header>
              <mat-card-title>
                <mat-icon>account_balance_wallet</mat-icon>
                Salary Calculation
              </mat-card-title>
              <mat-card-subtitle>
                Calculate monthly salaries based on attendance hours
              </mat-card-subtitle>
            </mat-card-header>
            
            <mat-card-content>
              <!-- Salary Settings -->
              <div class="salary-settings">
                <h4>Salary Calculation Settings</h4>
                <div class="settings-row">
                  <mat-form-field appearance="outline" class="setting-field">
                    <mat-label>Standard Hours</mat-label>
                    <input matInput type="number" [(ngModel)]="standardHours" (ngModelChange)="onSalarySettingsChange()" min="0" step="1">
                    <mat-hint>Hours before overtime rate applies</mat-hint>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="setting-field">
                    <mat-label>Overtime Rate (£)</mat-label>
                    <input matInput type="number" [(ngModel)]="overtimeRate" (ngModelChange)="onSalarySettingsChange()" min="0" step="0.01">
                    <mat-hint>Rate for hours above standard</mat-hint>
                  </mat-form-field>
                </div>
              </div>

              <!-- Salary Table -->
              <div class="salary-table-container" *ngIf="salaryCalculations.length > 0">
                <div class="table-wrapper">
                  <table class="salary-table">
                    <thead>
                      <tr>
                        <th class="employee-name-header">Employee</th>
                        <th class="salary-type-header">Type</th>
                        <th class="rate-header">Rate</th>
                        <th class="hours-header">Hours</th>
                        <th class="standard-hours-header">Standard Hours</th>
                        <th class="overtime-hours-header">Overtime Hours</th>
                        <th class="standard-pay-header">Standard Pay</th>
                        <th class="overtime-pay-header">Overtime Pay</th>
                        <th class="gross-pay-header">Gross Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let calc of salaryCalculations" class="salary-row">
                        <td class="employee-name-cell">{{ calc.employeeName }}</td>
                        <td class="salary-type-cell">
                          <span [class]="calc.salaryType === 2 ? 'monthly-badge' : 'hourly-badge'">
                            {{ calc.salaryType === 2 ? 'Monthly' : 'Hourly' }}
                          </span>
                        </td>
                        <td class="rate-cell">
                          <span *ngIf="calc.salaryType === 1">£{{ calc.hourlyRate | number:'1.2-2' }}/hr</span>
                          <span *ngIf="calc.salaryType === 2">£{{ calc.monthlySalary | number:'1.2-2' }}/month</span>
                        </td>
                        <td class="hours-cell">{{ calc.totalHours | number:'1.2-2' }}</td>
                        <td class="standard-hours-cell">{{ calc.standardHours }}</td>
                        <td class="overtime-hours-cell">{{ calc.overtimeHours | number:'1.2-2' }}</td>
                        <td class="standard-pay-cell">£{{ calc.standardPay | number:'1.2-2' }}</td>
                        <td class="overtime-pay-cell">£{{ calc.overtimePay | number:'1.2-2' }}</td>
                        <td class="gross-pay-cell">£{{ calc.grossPay | number:'1.2-2' }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="total-row">
                        <td class="total-label">TOTAL</td>
                        <td></td>
                        <td></td>
                        <td class="total-hours">{{ getTotalHours() | number:'1.2-2' }}</td>
                        <td></td>
                        <td class="total-overtime">{{ getTotalOvertimeHours() | number:'1.2-2' }}</td>
                        <td class="total-standard-pay">£{{ getTotalStandardPay() | number:'1.2-2' }}</td>
                        <td class="total-overtime-pay">£{{ getTotalOvertimePay() | number:'1.2-2' }}</td>
                        <td class="total-gross-pay">£{{ getTotalGrossPay() | number:'1.2-2' }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </div>

      <!-- No Data Message -->
      <div *ngIf="!reportData && !loading" class="no-data-message">
        <mat-icon>info</mat-icon>
        <p>Select a month and year to generate the attendance report</p>
      </div>
    </mat-card-content>
  </mat-card>
</div>
